import { TransformsList, Data } from 'cio/utils/analytics/transforms';
import { BeeTemplate } from 'cio/data/bee-templates';
import LayoutModel from 'cio/models/layout';
import TemplateModel from 'cio/models/template';
import CampaignModel from 'cio/models/campaign';
import NewsletterModel from 'cio/models/newsletter';
import TransactionalMessageModel from 'cio/models/transactional-message';
import { EmailEditor } from 'cio/services/editor';
import espInfo, { EspKey } from 'cio/utils/email-service-providers';
import { EditorNameMap } from 'cio/services/editor';

export enum CustomSmtpSetupAction {
  DeliveryProviderSelected = 'Delivery Provider Selected',
  ContinuedToSetup = 'Continued to Setup',
  ContinuedToAddCredentials = 'Continued to Add Credentials',
  SkippedToAddCredentials = 'Skipped to Add Credentials',
  AddedCredentials = 'Added Credentials',
  FinishedSetup = 'Finished Setup',
}

export enum CustomSmtpSetupStep {
  ProviderSelection = 'Provider Selection',
  SetupWebhooks = 'Setup Webhooks',
  AddCredentials = 'Add Credentials',
}

export type EmailCreationSource = 'preview_modal' | 'choose_template_page';

type BuilderData = Data & {
  templateToCopy?: TemplateModel | BeeTemplate;
  source?: EmailCreationSource;
  message: CampaignModel | NewsletterModel | TransactionalMessageModel;
  template: TemplateModel;
  editor: EmailEditor;
};

type SavedRowEventData = Data & {
  message: CampaignModel | NewsletterModel | TransactionalMessageModel;
  messageType: 'email'; // for now, this is just email, but could change if we support saved rows in other composers
  template: TemplateModel;
  editor: EmailEditor;
};

const EmailCreationSourceMap: Record<EmailCreationSource, string> = {
  preview_modal: 'Preview Modal',
  choose_template_page: 'Editor Page',
};

function generateFrom(template?: TemplateModel | BeeTemplate | LayoutModel): string {
  if (!template) {
    return 'Scratch';
  }

  if (template instanceof LayoutModel) {
    return 'Layout';
  }

  if (template instanceof BeeTemplate) {
    return 'Template';
  }

  return 'Existing Email';
}

function getTemplateName(template?: TemplateModel | BeeTemplate | LayoutModel): string | undefined {
  if (!template) {
    return undefined;
  }

  if (template instanceof BeeTemplate || template instanceof LayoutModel) {
    return template.name;
  }

  if (template.action) {
    return template.action.name;
  }

  if (template.newsletter) {
    return template.newsletter.name;
  }

  return;
}

function getMessageType(message: CampaignModel | NewsletterModel | TransactionalMessageModel) {
  if (message instanceof CampaignModel) {
    return message.isApiTriggered ? 'Triggered Broadcast' : 'Campaign';
  } else if (message instanceof NewsletterModel) {
    return 'Newsletter';
  } else {
    return 'Transactional Message';
  }
}

function getParentDetails(data: BuilderData) {
  let { message, template } = data;

  if (message instanceof CampaignModel) {
    return {
      'Campaign ID': message.id,
      'Campaign Action ID': template.action?.id,
    };
  } else if (message instanceof NewsletterModel) {
    return {
      'Newsletter ID': message.id,
      'Newsletter Content ID': template?.id,
    };
  } else {
    return {
      'Transactional Message ID': message.id,
      'Transactional Message Content ID': template?.id,
    };
  }
}

const Transforms: TransformsList = {
  savedRowEvent(data: SavedRowEventData) {
    return {
      ...getParentDetails(data),
      'Message Used In': getMessageType(data.message),
      'Message Type': data.messageType,
      'Builder Type': EditorNameMap[data.editor],
    };
  },

  builderChosen(data: BuilderData) {
    return {
      ...getParentDetails(data),
      Source: EmailCreationSourceMap[data.source ?? 'choose_template_page'],
      'Start From': generateFrom(data.templateToCopy),
      'Template Name': getTemplateName(data.templateToCopy),
      'Message Used In': getMessageType(data.message),
      'Builder Type': EditorNameMap[data.editor],
    };
  },

  previewTemplateClicked(
    data: Data & {
      template: TemplateModel | LayoutModel;
      message: CampaignModel | NewsletterModel;
      previewType: 'Template' | 'Layout';
    },
  ) {
    return {
      'Template Name': getTemplateName(data.template),
      'Message Used In': getMessageType(data.message),
      'Preview Type': data.previewType,
    };
  },

  searchPerformed(data: Data & { searchTerm: string; resultsFound: number }) {
    return {
      'Search Term': data.searchTerm,
      'Results Found': data.resultsFound,
      'Message Used In': getMessageType(data.message),
    };
  },

  customSmtpAddServerSetupActionComplete(data: Data & { action: CustomSmtpSetupAction; esp: EspKey | null }) {
    return {
      'Custom SMTP Setup Action': data.action,
      'Custom SMTP Server': data.esp != null ? espInfo(data.esp).name : '',
    };
  },

  customSmtpAddServerSetupCanceled(data: Data & { step: CustomSmtpSetupStep; esp: EspKey | null }) {
    return {
      'Custom SMTP Setup Step': data.step,
      'Custom SMTP Server': data.esp != null ? espInfo(data.esp).name : '',
    };
  },

  htmlToggled(data: BuilderData & { toggledOn: boolean }) {
    return {
      ...getParentDetails(data),
      'Message Used In': getMessageType(data.message),
      'Toggled On': data.toggledOn,
    };
  },
};

export default Transforms;
